import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import type {
  AssignableAdminUser,
  FileAssignmentAssignPayload,
  FileAssignmentRecord,
} from '../../../../domain/src/lib/repository/file-assignment.model';
import {
  resolveTenantId,
  resolveVaultApiTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';
import {
  vaultUserIsSuperadminFromSession,
  VAULT_FILE_ASSIGNMENTS_CHANGED_EVENT,
} from '@nebula/shared-vault/data-access';
import {
  extractAdminUsersArray,
  normalizeAdminUserTenantsFromRecord,
} from '../admin-users/admin-user-normalize.util';

interface AssignedCacheSnapshot {
  key: string;
  fetchedAt: number;
  rows: FileAssignmentRecord[];
}

export interface FileAssignmentQueryOptions {
  /** Omite caché reciente y peticiones en vuelo (tras asignar/desasignar o volver a la pestaña). */
  force?: boolean;
  /**
   * Separa caché admin (listado global) vs usuario operativo (JWT).
   * Sin esto, superadmin y consultor comparten clave `tenant||`.
   */
  cacheNamespace?: string;
}

@Injectable({ providedIn: 'root' })
export class FileAssignmentService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly basePath = '/api/file-assignments';
  private readonly usersPath = '/api/admin/users';

  private static readonly ASSIGNED_STALE_MS = 5_000;

  private readonly assignedSnapshots = new Map<string, AssignedCacheSnapshot>();
  private readonly assignedInFlight = new Map<string, Observable<FileAssignmentRecord[]>>();

  constructor() {
    if (typeof globalThis.window === 'undefined') {
      return;
    }
    const invalidate = () => this.invalidateAssignedCache();
    globalThis.window.addEventListener(VAULT_FILE_ASSIGNMENTS_CHANGED_EVENT, invalidate);
    globalThis.window.addEventListener('vault-session-updated', invalidate);
  }

  invalidateAssignedCache(): void {
    this.assignedSnapshots.clear();
    this.assignedInFlight.clear();
  }

  assign(payload: FileAssignmentAssignPayload): Observable<unknown> {
    const tenantId = (payload.tenant_id || resolveVaultApiTenantId(this.serverContext)).trim();
    const p = withTenantParam(tenantId);
    return this.http.post(`${this.basePath}/assign`, payload, {
      headers: tenantHeaders(tenantId),
      ...(p ? { params: p } : {}),
    });
  }

  /**
   * Lista usuarios para asignación.
   * Superadmin: listado global (paridad Gestión de Usuarios). Resto: filtrado por tenant.
   */
  listUsersForAssign(globalScope?: boolean): Observable<AssignableAdminUser[]> {
    const scope = globalScope ?? vaultUserIsSuperadminFromSession();
    const tenantId = scope ? '' : resolveTenantId(this.serverContext);
    let params = withTenantParam(tenantId) ?? new HttpParams();
    params = params.set('_', String(Date.now()));
    return this.http
      .get<unknown>(this.usersPath, {
        headers: tenantHeaders(tenantId),
        params,
      })
      .pipe(map((raw) => this.normalizeUsers(raw)));
  }

  getAssigned(
    userId?: string,
    role?: string,
    options?: FileAssignmentQueryOptions,
  ): Observable<FileAssignmentRecord[]> {
    const tenantId = resolveVaultApiTenantId(this.serverContext);
    const cacheKey = this.assignedCacheKey(
      tenantId,
      userId,
      role,
      options?.cacheNamespace,
    );
    const force = options?.force === true;

    const snapshot = this.assignedSnapshots.get(cacheKey);
    if (!force && snapshot) {
      const age = Date.now() - snapshot.fetchedAt;
      if (age < FileAssignmentService.ASSIGNED_STALE_MS) {
        return of(snapshot.rows);
      }
    }

    if (force) {
      this.assignedInFlight.delete(cacheKey);
    } else {
      const inflight = this.assignedInFlight.get(cacheKey);
      if (inflight) {
        return inflight;
      }
    }

    let params = withTenantParam(tenantId) ?? new HttpParams();
    if (userId) params = params.set('user_id', userId);
    if (role) params = params.set('role', role);
    params = params.set('_', String(Date.now()));

    const request$ = this.http
      .get<unknown>(`${this.basePath}/assigned`, {
        headers: tenantHeaders(tenantId),
        params,
      })
      .pipe(
        map((raw) => this.normalizeList(raw)),
        tap((rows) => {
          this.assignedSnapshots.set(cacheKey, {
            key: cacheKey,
            fetchedAt: Date.now(),
            rows,
          });
        }),
        finalize(() => {
          this.assignedInFlight.delete(cacheKey);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.assignedInFlight.set(cacheKey, request$);
    return request$;
  }

  private assignedCacheKey(
    tenantId: string,
    userId?: string,
    role?: string,
    namespace?: string,
  ): string {
    const ns = namespace ?? (userId ? `uid:${userId}` : 'global');
    return `${tenantId}|${ns}|${role ?? ''}`;
  }

  unassign(assignmentId: string): Observable<unknown> {
    const tenantId = resolveVaultApiTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http.delete(`${this.basePath}/unassign/${assignmentId}`, {
      headers: tenantHeaders(tenantId),
      ...(p ? { params: p } : {}),
    });
  }

  private normalizeUsers(raw: unknown): AssignableAdminUser[] {
    const list = extractAdminUsersArray(raw);
    return list.map((item) => {
      const r = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      const tenants = normalizeAdminUserTenantsFromRecord(r).map((t) => ({
        tenant_id: t.tenant_id,
        role: t.role,
        tenant_name: t.tenant_name,
      }));
      return {
        id: String(r['id'] ?? ''),
        email: String(r['email'] ?? ''),
        full_name: String(r['full_name'] ?? r['fullName'] ?? r['name'] ?? ''),
        is_active: r['is_active'] !== false,
        is_superadmin: Boolean(r['is_superadmin']),
        tenants,
      };
    });
  }

  private normalizeList(raw: unknown): FileAssignmentRecord[] {
    if (Array.isArray(raw)) return raw.map((item) => this.normalizeRecord(item));
    if (raw && typeof raw === 'object') {
      const data = (raw as { data?: unknown }).data;
      if (Array.isArray(data)) return data.map((item) => this.normalizeRecord(item));
    }
    return [];
  }

  private normalizeRecord(raw: unknown): FileAssignmentRecord {
    const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
      ...r,
      id: String(r['id'] ?? ''),
      file_path: String(r['file_path'] ?? r['filePath'] ?? ''),
      tenant_id: typeof r['tenant_id'] === 'string' ? r['tenant_id'] : undefined,
      user_id: normalizeAssignmentUserId(
        r['user_id'] ?? r['userId'] ?? r['assigned_user_id'],
      ),
      role: typeof r['role'] === 'string' ? r['role'] : undefined,
      is_directory: r['is_directory'] === true,
      expires_at: typeof r['expires_at'] === 'string' ? r['expires_at'] : null,
    };
  }
}

function normalizeAssignmentUserId(raw: unknown): string | undefined {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed || undefined;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(Math.trunc(raw));
  }
  return undefined;
}
